<template>
   <h2>ATB Form</h2>
    <div class="atb-form-input">
          <label for="search_antibiotic">Antimicrobial:</label>
          <div class="dropdown-container">
          <input type="text"
            v-model="store.antibioticText"
            placeholder="Search..."
            class="search-box"
            @focus="showDropdown = true"
            @click="showDropdown = true"
            @blur="showDropdown = false"
          />
          <ul v-if="showDropdown" class="dropdown-list">
            <li
              v-for="option in filteredOptions"
              :key="option"
              @mousedown.prevent="selectOption(option)"
              class="dropdown-item"
            >
              {{ option["atb"] }}
            </li>
          </ul>
       </div>
        </div>
        <div class="atb-form-input">
          <label for="search_species">Species:</label>
          <div class="dropdown-container">
          <input type="text"
            v-model="store.speciesText"
            placeholder="Search..."
            class="search-box"
            @focus="showDropdownSpecies = true"
            @click="showDropdownSPecies = true"
            @blur="showDropdownSpecies = false"
          />
          <ul v-if="showDropdownSpecies" class="dropdown-list">
            <li
              v-for="option in filteredOptionsSpecies"
              :key="option"
              @mousedown.prevent="selectOptionSpecies(option)"
              class="dropdown-item"
            >
            <p :style="{ color: /\d/.test(option[1]) ? 'green' : 'red', margin: '0px' }">
  {{ option[0] }}
</p>
            </li>
          </ul>
       </div>
        </div>
        <br>
        <!-- Conditional Ctrough input field -->
        <div class="atb-form-input" v-if="store.currentAtbDataRow && store.currentAtbDataRow['Ctrough'] == '1'">
          <label>Total Concentration (mg/L):</label>
          <div class="input-container">
            <input type="number" v-model="store.measuredCon" class="concentration-input">
          </div>
        </div>
        
        <!-- Continuous checkbox (only shows when Trough is visible) -->
        <div class="atb-form-input" v-if="store.currentAtbDataRow && store.currentAtbDataRow['Ctrough'] == '1'  && store.currentAtbDataRow['beta-lactamin '] == '1' ">
          <label style="margin-left: 10px;">Continuous ?</label>
          <div class="input-container">
            <input type="checkbox" v-model="isContinuous" @change="updateModeAdmin" class="checkbox-input" :disabled="isContinuousDisabled">
          </div>
        </div>
        
        <!-- Conditional Cpeak input field -->
        <div class="atb-form-input" v-if="store.currentAtbDataRow && store.currentAtbDataRow['Cpeak'] == '1'">
          <label>Peak Concentration (mg/L):</label>
          <div class="input-container">
            <input type="number" v-model="store.peakConcentration" class="concentration-input">
          </div>
        </div>
        
        <!-- Conditional AUC input field -->
        <div class="atb-form-input" v-if="store.currentAtbDataRow && store.currentAtbDataRow['AUC'] == '1'">
          <label>Estimated 24-hours AUC (mg.h/L):</label>
          <div class="input-container">
            <input type="number" v-model="store.estimatedAUC" class="concentration-input">
          </div>
        </div>
</template>

<script setup>
import { ref, watch, computed } from "vue"
import { useTestStore } from "@/stores/testStore";

const store = useTestStore();

const showDropdown = ref(false);
const showDropdownSpecies = ref(false);
const isContinuous = ref(store.modeAdmin === "IVSE");

// Computed property to determine if continuous checkbox should be disabled
const isContinuousDisabled = computed(() => {
  // Disable if admin mode is PO
  if (store.modeAdmin === "PO") {
    return true;
  }
  // Disable if infusion duration is not 24 (and not null/undefined)
  if (store.durationIV !== null && store.durationIV !== undefined && store.durationIV !== 24) {
    return true;
  }
  return false;
});

// Computed property for filtering options
const filteredOptions = computed(() =>{
  return store.atbSpeciesPairInfo.filter(row =>
    row["atb"].toLowerCase().includes(store.antibioticText.toLowerCase())
    
  )
}
);

const filteredOptionsSpecies = computed(() =>{
  let row = "";
  row = store.atbSpeciesPairInfo.find(row => row["atb"] === store.antibioticText);
  if (row) {
    const allSpecies = Object.entries(row).filter(([key, value]) => key !== "atb" && key !== "value" && key !== "Species...");
    const filtered = allSpecies.filter(species =>
      species[0].toLowerCase().includes(store.speciesText.toLowerCase()));
    
    // Separate green (available) and red (unavailable) species
    const greenSpecies = filtered.filter(species => /\d/.test(species[1]));
    const redSpecies = filtered.filter(species => !/\d/.test(species[1]));
    
    // Sort each group alphabetically
    greenSpecies.sort((a, b) => a[0].localeCompare(b[0]));
    redSpecies.sort((a, b) => a[0].localeCompare(b[0]));
    
    // Return green species first, then red species
    return [...greenSpecies, ...redSpecies];
  }
  else {
    return [["select an antibiotic first"]];
  }
});

// Function to select an option
const selectOption = (option) => {
  store.antibioticText = option["atb"]; // Set input value
  showDropdown.value = false; // Hide dropdown
  store.antibioticValue = option["value"];
};

const selectOptionSpecies = (option) => {
  store.speciesText = option[0]; // Set input value
  showDropdownSpecies.value = false; // Hide dropdown
};

// Watcher to reset species when antibiotic changes
watch(() => store.antibioticText, () => {
  store.speciesText = '';
});

// Watcher to update continuous checkbox when store.modeAdmin changes
watch(() => store.modeAdmin, (newValue) => {
  isContinuous.value = newValue === "IVSE";
});

// Watcher to uncheck continuous when it becomes disabled
watch(isContinuousDisabled, (isDisabled) => {
  if (isDisabled && isContinuous.value) {
    isContinuous.value = false;
    store.modeAdmin = "IV";
  }
});

// Watcher to check continuous when duration is set to 24
watch(() => store.durationIV, (newDuration) => {
  if (newDuration === 24 && store.modeAdmin === "IV" && !isContinuousDisabled.value) {
    isContinuous.value = true;
    store.modeAdmin = "IVSE";
  }
});

// Function to update modeAdmin based on checkbox
const updateModeAdmin = () => {
  if (isContinuous.value) {
    store.modeAdmin = "IVSE";
    store.durationIV = 24;
  } else {
    store.modeAdmin = "IV";
  }
};

</script>



<style scoped>
.atb-form-input {
  display: flex;
  align-items: center;
  margin: 3px 0px 10px 10px;
}

.atb-form-input label {
  width: 250px;
  flex-shrink: 0;
  text-align: left;
}

.dropdown-container {
  position: relative;
  flex: 1;
  max-width: 200px;
  font-family: Arial, sans-serif;
}

.input-container {
  position: relative;
  flex: 0 0 auto;
  font-family: Arial, sans-serif;
}

.concentration-input {
  width: 100px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: left;
  box-sizing: border-box;
}

.checkbox-input {
  width: auto;
  padding: 0;
  margin: 0;
  transform: scale(1.2);
}
.search-box {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  box-sizing: border-box;
}
.dropdown-list {
  z-index: 1000;
  position: absolute;
  width: 100%;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  list-style: none;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}
.dropdown-item {
  padding: 8px;
  cursor: pointer;
}
.dropdown-item:hover {
  background-color: #007bff;
  color: white;
}
</style>